import { Users, MoreVertical, Edit, ToggleLeft, ToggleRight, Trash2, Circle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { RestaurantTable } from "@/hooks/useTables";

interface TableCardProps {
  table: RestaurantTable;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

const statusConfig = {
  available: {
    bg: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
    badge: "bg-emerald-500 hover:bg-emerald-500",
    label: "Disponível",
    dot: "text-emerald-500",
  },
  occupied: {
    bg: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
    badge: "bg-amber-500 hover:bg-amber-500",
    label: "Ocupada",
    dot: "text-amber-500",
  },
  reserved: {
    bg: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
    badge: "bg-blue-500 hover:bg-blue-500",
    label: "Reservada",
    dot: "text-blue-500",
  },
};

export function TableCard({ table, onEdit, onToggleActive, onDelete }: TableCardProps) {
  const config = statusConfig[table.status];

  return (
    <Card
      className={cn(
        "relative transition-all hover:shadow-md border-2",
        table.is_active ? config.bg : "opacity-50 bg-muted border-muted"
      )}
    >
      <CardContent className="p-4">
        {/* Status Indicator */}
        {table.is_active && (
          <div className="absolute top-2 left-2">
            <Circle className={cn("h-3 w-3 fill-current", config.dot)} />
          </div>
        )}

        {/* Menu */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onToggleActive}>
                {table.is_active ? (
                  <>
                    <ToggleLeft className="h-4 w-4 mr-2" />
                    Desativar
                  </>
                ) : (
                  <>
                    <ToggleRight className="h-4 w-4 mr-2" />
                    Ativar
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="text-center pt-4">
          <span className="text-3xl font-bold">{table.number}</span>
          {table.name && (
            <p className="text-xs text-muted-foreground mt-1 truncate">{table.name}</p>
          )}
          
          <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />
            <span>{table.capacity}</span>
          </div>

          {table.is_active && (
            <Badge className={cn("mt-2 text-white", config.badge)}>
              {config.label}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
